"use client"

/**
 * Soft 404 — rendered INSIDE the Shell so tabs, sidebar, and the
 * WebSocket stay alive. Two callers:
 *
 *   1. The Next.js route-group convention component at
 *      `app/(main)/not-found.tsx` re-exports this with no props,
 *      so unmatched URLs hit the default "Page not found" copy.
 *
 *   2. Detail views (ChatView, ModelDetailView, etc.) render this
 *      with specialised props when a route MATCHED but the underlying
 *      entity doesn't exist in the DB — e.g. /chat/<bogus-id> shows
 *      a "Chat not found" variant.
 *
 * All props are optional; defaults match the original NotFound copy.
 */

import Link from 'next/link';

interface NotFoundProps {
  title?: string;
  description?: string;
  linkText?: string;
  linkHref?: string;
}

export default function NotFound({
  title = 'Page not found',
  description = "The page you're looking for doesn't exist or has been moved.",
  linkText = 'Go to workspace',
  linkHref = '/',
}: NotFoundProps) {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <h2 className="text-2xl font-semibold text-gray-800 mb-2">{title}</h2>
      <p className="text-sm text-gray-500 mb-4">{description}</p>
      <Link
        href={linkHref}
        className="text-sm text-teal-600 hover:text-teal-700 underline"
      >
        {linkText}
      </Link>
    </div>
  );
}
